import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Download,
  Eye,
  FileText,
  Loader2,
  RotateCcw,
  XCircle,
} from 'lucide-react';
import ConfirmModal from '../UI/ConfirmModal';
import DebouncedSearchInput from '../UI/DebouncedSearchInput';
import { FilterButton } from '../UI/FilterButton';
import LoadingSpinner from '../UI/LoadingSpinner';
import { Pagination } from '../UI/Pagination';
import PdfViewer from '../UI/PdfViewer';
import SelfServiceWorklistSkeleton from '../UI/SelfServiceWorklistSkeleton';
import { StatusBadge } from '../UI/StatusBadge';
import { TradeBadge } from '../UI/TradeBadge';
import {
  DOCUMENT_REQUEST_TYPE_OPTIONS,
  getDocumentRequestPurposeLabel,
  getDocumentRequestType,
} from '../../config/documentRequests';
import { isTradeRedundantWithDepartment } from '../../config/tradeConfig';
import {
  readDocumentRequestError,
  useApproveDocumentRequest,
  useDocumentRequestPreview,
  useDocumentRequestQueue,
  useHRLetterPreview,
  useRegenerateHRLetter,
  useRejectDocumentRequest,
  useRetryDocumentRequestGeneration,
} from '../../hooks/useDocumentRequestData';
import { useIsMobile } from '../../hooks/useIsMobile';
import api from '../../services/api';
import toast from '../../utils/toast';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { getLetterExpiryStatus } from '../../utils/letterExpiryStatus';

const PAGE_SIZE = 20;

const itemDate = (item) =>
  new Date(
    item.itemType === 'letter' ? item.generated_at : item.created_at,
  ).getTime();

const itemTypeLabel = (item) =>
  item.request_type_display
  || item.letter_type_display
  || getDocumentRequestType(item.request_type).label;

const mergeQueueItems = ({ requests = [], letters = [] }) => {
  const requestIdsWithLetters = new Set(
    letters
      .map((letter) => letter.request_id)
      .filter(Boolean)
      .map(String),
  );
  const requestItems = requests
    .filter(
      (request) =>
        request.status === 'submitted'
        || (
          request.status === 'approved'
          && !requestIdsWithLetters.has(String(request.id))
        ),
    )
    .map((request) => ({ ...request, itemType: 'request' }));
  const letterItems = letters.map((letter) => ({
    ...letter,
    itemType: 'letter',
  }));
  return [...requestItems, ...letterItems].sort(
    (left, right) => itemDate(right) - itemDate(left),
  );
};

const matchesSearch = (item, query) => {
  if (!query) return true;
  const value = [
    item.staff_name,
    item.staff_employee_id,
    item.department_name,
    item.trade_name,
    itemTypeLabel(item),
    getDocumentRequestPurposeLabel(item),
    item.letter_reference,
    item.template_code,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return value.includes(query.toLowerCase());
};

const queueStatus = (item) => {
  if (item.itemType === 'letter') {
    return item.file_purged_at
      ? { status: 'inactive', label: 'PDF removed' }
      : { status: 'completed', label: 'Generated' };
  }
  if (item.status === 'approved' && !item.has_letter) {
    return { status: 'failed', label: 'Generation issue' };
  }
  return { status: 'submitted', label: 'Pending review' };
};

const QueueCard = ({ item, selected, onSelect }) => {
  const status = queueStatus(item);
  return (
    <button
      type="button"
      data-selected={selected ? 'true' : undefined}
      onClick={() => onSelect(item)}
      className={`w-full text-left bg-white rounded-2xl border p-4 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 ${
        selected
          ? 'border-red-600 shadow-md'
          : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">
            {item.staff_name || 'Employee unavailable'}
          </p>
          <p className="text-xs text-gray-500 mt-0.5 truncate">
            {item.staff_employee_id || 'No staff ID'}
            {item.department_name ? ` · ${item.department_name}` : ''}
          </p>
        </div>
        <StatusBadge
          status={status.status}
          label={status.label}
          showIcon
        />
      </div>
      <div className="mt-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-gray-800 truncate">{itemTypeLabel(item)}</p>
          <p className="text-xs text-gray-500 mt-0.5 truncate">
            {getDocumentRequestPurposeLabel(item)}
          </p>
        </div>
        <p className="text-xs text-gray-500 tabular-nums flex-shrink-0">
          {formatDate(
            item.itemType === 'letter'
              ? item.generated_at
              : item.created_at,
          )}
        </p>
      </div>
    </button>
  );
};

const PreviewPanel = ({ item }) => {
  const requestPreview = useDocumentRequestPreview(
    item?.id,
    item?.itemType === 'request',
  );
  const letterPreview = useHRLetterPreview(
    item?.id,
    item?.itemType === 'letter' && !item?.file_purged_at,
  );
  const [blobUrl, setBlobUrl] = useState(null);
  const activePreview = item?.itemType === 'letter'
    ? letterPreview
    : requestPreview;

  useEffect(() => {
    if (!activePreview.data) {
      setBlobUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(
      new Blob([activePreview.data], { type: 'application/pdf' }),
    );
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [activePreview.data]);

  if (item?.itemType === 'letter' && item.file_purged_at) {
    return (
      <div className="h-[420px] rounded-xl border border-gray-200 bg-gray-50 flex flex-col items-center justify-center text-center px-6">
        <FileText className="w-12 h-12 text-gray-300 mb-3" />
        <p className="text-sm font-medium text-gray-900">PDF removed by retention policy</p>
        <p className="text-sm text-gray-500 mt-1 max-w-sm">
          The audit record remains. Restore the PDF only when there is a valid HR need.
        </p>
      </div>
    );
  }

  if (activePreview.isLoading) {
    return (
      <div className="h-[420px] rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center gap-3">
        <LoadingSpinner size="md" />
        <span className="text-sm text-gray-500">Preparing document preview…</span>
      </div>
    );
  }

  if (activePreview.isError || !blobUrl) {
    return (
      <div className="h-[420px] rounded-xl border border-gray-200 bg-gray-50 flex flex-col items-center justify-center text-center px-6">
        <AlertCircle className="w-12 h-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Preview unavailable
        </h3>
        <p className="text-sm text-gray-500">
          Check the template and employee data, then try again.
        </p>
        <button
          type="button"
          onClick={() => activePreview.refetch()}
          className="btn btn-admin-outline mt-4"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="h-[60dvh] min-h-[420px] rounded-xl border border-gray-200 overflow-hidden bg-gray-100">
      <PdfViewer
        src={blobUrl}
        title={itemTypeLabel(item)}
        fileName={
          item.itemType === 'letter'
            ? `${item.template_code || 'hr_letter'}_${item.id}.pdf`
            : `request_${item.id}.pdf`
        }
        theme="admin"
      />
    </div>
  );
};

const DetailField = ({ label, children }) => (
  <div className="min-w-0">
    <dt className="text-xs text-gray-500">{label}</dt>
    <dd className="text-sm text-gray-900 mt-1 break-words">{children || '—'}</dd>
  </div>
);

const RequestDetail = ({
  item,
  onApprove,
  onReject,
  onRetry,
  approvePending,
  retryPending,
}) => {
  const status = queueStatus(item);
  const isGenerationIssue = item.status === 'approved' && !item.has_letter;
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900 truncate">
            {item.staff_name || 'Employee unavailable'}
          </h2>
          <p className="text-sm text-gray-500 mt-0.5 truncate">
            {item.staff_employee_id || 'No staff ID'}
          </p>
        </div>
        <StatusBadge
          status={status.status}
          label={status.label}
          showIcon
        />
      </div>

      {isGenerationIssue && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-900">
              This legacy approval has no generated letter
            </p>
            <p className="text-sm text-red-700 mt-1">
              Verify the preview and template, then retry generation.
            </p>
          </div>
        </div>
      )}

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-xl bg-gray-50 border border-gray-200 p-4">
        <DetailField label="Letter type">{itemTypeLabel(item)}</DetailField>
        <DetailField label="Purpose">
          {getDocumentRequestPurposeLabel(item)}
        </DetailField>
        <DetailField label="Department">{item.department_name}</DetailField>
        <DetailField label="Trade">
          {item.trade_name
            && !isTradeRedundantWithDepartment(
              item.department_name,
              null,
              item.trade_name,
            )
            ? <TradeBadge name={item.trade_name} size="xs" />
            : '—'}
        </DetailField>
        <DetailField label="Requested">
          <span className="tabular-nums">{formatDateTime(item.created_at)}</span>
        </DetailField>
        <DetailField label="Template">{item.template_name}</DetailField>
      </dl>

      <div>
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Generated preview</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Review the exact employee-facing PDF before deciding.
            </p>
          </div>
          <Eye className="w-5 h-5 text-gray-400 flex-shrink-0" />
        </div>
        <PreviewPanel item={item} />
      </div>

      <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 pt-4 border-t border-gray-200">
        {isGenerationIssue ? (
          <button
            type="button"
            onClick={onRetry}
            disabled={retryPending}
            className="btn btn-admin-primary"
          >
            {retryPending
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <RotateCcw className="w-4 h-4" />}
            Retry generation
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={onReject}
              disabled={approvePending}
              className="btn btn-danger"
            >
              <XCircle className="w-4 h-4" />
              Reject
            </button>
            <button
              type="button"
              onClick={onApprove}
              disabled={approvePending}
              className="btn btn-success"
            >
              {approvePending
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <CheckCircle className="w-4 h-4" />}
              Approve and generate
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const LetterDetail = ({
  item,
  onDownload,
  onRegenerate,
  downloading,
  regenerating,
}) => {
  const expiry = getLetterExpiryStatus(item.valid_until, item.file_purged_at);
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900 truncate">
            {item.staff_name || 'Employee unavailable'}
          </h2>
          <p className="text-sm text-gray-500 mt-0.5 truncate">
            {item.letter_reference || item.staff_employee_id || 'Generated letter'}
          </p>
        </div>
        <StatusBadge
          status={item.file_purged_at ? 'inactive' : 'completed'}
          label={item.file_purged_at ? 'PDF removed' : 'Generated'}
          showIcon
        />
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-xl bg-gray-50 border border-gray-200 p-4">
        <DetailField label="Letter type">{itemTypeLabel(item)}</DetailField>
        <DetailField label="Purpose">
          {getDocumentRequestPurposeLabel(item)}
        </DetailField>
        <DetailField label="Generated">
          <span className="tabular-nums">{formatDateTime(item.generated_at)}</span>
        </DetailField>
        <DetailField label="Employee validity">
          {expiry
            ? (
              <StatusBadge
                status={
                  expiry.purged
                    ? 'inactive'
                    : expiry.canDownload
                      ? (expiry.tone === 'expiring' ? 'pending' : 'verified')
                      : 'failed'
                }
                label={expiry.purged ? 'Removed' : expiry.label}
              />
            )
            : '—'}
        </DetailField>
        <DetailField label="Template">
          {item.template_code ? `${item.template_code} v${item.template_version}` : '—'}
        </DetailField>
        <DetailField label="Retention until">
          <span className="tabular-nums">{formatDate(item.retention_until)}</span>
        </DetailField>
      </dl>

      <div>
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Issued PDF</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              HR access remains available for audit and operational review.
            </p>
          </div>
          <Eye className="w-5 h-5 text-gray-400 flex-shrink-0" />
        </div>
        <PreviewPanel item={item} />
      </div>

      <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 pt-4 border-t border-gray-200">
        {item.file_purged_at && (
          <button
            type="button"
            onClick={onRegenerate}
            disabled={regenerating}
            className="btn btn-admin-outline"
          >
            {regenerating
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <RotateCcw className="w-4 h-4" />}
            Restore PDF
          </button>
        )}
        {!item.file_purged_at && (
          <button
            type="button"
            onClick={onDownload}
            disabled={downloading}
            className="btn btn-admin-primary"
          >
            {downloading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Download className="w-4 h-4" />}
            Download
          </button>
        )}
      </div>
    </div>
  );
};

const DocumentRequestQueue = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('id') || '';
  const selectedKind = searchParams.get('kind') || '';
  const isCompact = useIsMobile(1024);
  const listPaneRef = useRef(null);
  const detailPaneRef = useRef(null);
  const [filters, setFilters] = useState({ query: '', status: '', type: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [confirm, setConfirm] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [downloading, setDownloading] = useState(false);

  const {
    data = { requests: [], letters: [] },
    isLoading,
    isError,
    refetch,
  } = useDocumentRequestQueue();
  const approve = useApproveDocumentRequest();
  const reject = useRejectDocumentRequest();
  const retry = useRetryDocumentRequestGeneration();
  const regenerate = useRegenerateHRLetter();

  const allItems = useMemo(() => mergeQueueItems(data), [data]);
  const filteredItems = useMemo(
    () => allItems.filter((item) => {
      if (!matchesSearch(item, filters.query)) return false;
      if (filters.type && item.request_type !== filters.type) return false;
      if (filters.status === 'pending') {
        return item.itemType === 'request' && item.status === 'submitted';
      }
      if (filters.status === 'generation_issue') {
        return (
          item.itemType === 'request'
          && item.status === 'approved'
          && !item.has_letter
        );
      }
      if (filters.status === 'generated') return item.itemType === 'letter';
      return true;
    }),
    [allItems, filters],
  );
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const visibleItems = filteredItems.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );
  const selectedItem = allItems.find(
    (item) =>
      String(item.id) === selectedId
      && (!selectedKind || item.itemType === selectedKind),
  );
  const pendingCount = data.requests.filter(
    (request) => request.status === 'submitted',
  ).length;
  const generatedCount = data.letters.length;
  const activeFilterCount = [
    filters.query,
    filters.status,
    filters.type,
  ].filter(Boolean).length;

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    if (isCompact || !selectedId || isLoading) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const pane = listPaneRef.current;
      const card = pane?.querySelector('[data-selected="true"]');
      if (!pane || !card) return;
      const paneRect = pane.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      if (cardRect.top < paneRect.top) {
        pane.scrollTop += cardRect.top - paneRect.top;
      } else if (cardRect.bottom > paneRect.bottom) {
        pane.scrollTop += cardRect.bottom - paneRect.bottom;
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isCompact, selectedId, isLoading]);

  useEffect(() => {
    if (!isCompact && selectedId) detailPaneRef.current?.scrollTo({ top: 0 });
  }, [isCompact, selectedId]);

  const selectItem = (item) => {
    const next = new URLSearchParams(searchParams);
    next.set('id', String(item.id));
    next.set('kind', item.itemType);
    setSearchParams(next);
  };

  const closeDetail = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('id');
    next.delete('kind');
    setSearchParams(next);
  };

  const clearFilters = () => {
    setFilters({ query: '', status: '', type: '' });
    setShowFilters(false);
  };

  const downloadLetter = async () => {
    if (!selectedItem || downloading) return;
    setDownloading(true);
    try {
      const response = await api.downloadHRLetter(selectedItem.id);
      const url = URL.createObjectURL(
        new Blob([response.data], { type: 'application/pdf' }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedItem.template_code || 'hr_letter'}_v${selectedItem.template_version || 1}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(
        await readDocumentRequestError(
          error,
          'The letter could not be downloaded.',
        ),
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleConfirm = async () => {
    if (!confirm?.item) return;
    if (confirm.type === 'reject' && !rejectionReason.trim()) {
      toast.error('Enter a reason for rejection');
      return;
    }
    try {
      if (confirm.type === 'approve') {
        await approve.mutateAsync(confirm.item.id);
      } else if (confirm.type === 'reject') {
        await reject.mutateAsync({
          id: confirm.item.id,
          reason: rejectionReason.trim(),
        });
      } else if (confirm.type === 'retry') {
        await retry.mutateAsync(confirm.item.id);
      } else if (confirm.type === 'regenerate') {
        await regenerate.mutateAsync(confirm.item.id);
      }
    } catch {
      return;
    }
    setConfirm(null);
    setRejectionReason('');
    closeDetail();
  };

  const mutationPending =
    approve.isPending
    || reject.isPending
    || retry.isPending
    || regenerate.isPending;

  return (
    <>
      <div className="flex flex-col gap-4 lg:flex-1 lg:min-h-0 lg:overflow-hidden">
        {(!isCompact || !selectedId) && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm flex-shrink-0">
            <div className="p-3 border-b border-gray-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <DebouncedSearchInput
                value={filters.query}
                onSearch={(query) =>
                  setFilters((current) => ({ ...current, query }))}
                placeholder="Search employee, ID, type, purpose, or reference..."
                theme="admin"
                className="w-full sm:max-w-xl min-w-0"
              />
              <FilterButton
                theme="admin"
                isOpen={showFilters}
                activeCount={activeFilterCount}
                onClick={() => setShowFilters((current) => !current)}
              />
            </div>

            <div className="px-4 py-3 flex flex-wrap items-center gap-2 border-b border-gray-100">
              <StatusBadge status="submitted" label={`${pendingCount} pending`} />
              <StatusBadge status="completed" label={`${generatedCount} generated`} />
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="btn btn-admin-outline btn-sm ml-auto"
                >
                  Clear filters
                </button>
              )}
            </div>

            {showFilters && (
              <div className="p-4 bg-gray-50 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Status</label>
                  <select
                    className="select-admin w-full"
                    value={filters.status}
                    onChange={(event) =>
                      setFilters((current) => ({
                        ...current,
                        status: event.target.value,
                      }))}
                  >
                    <option value="">All queue records</option>
                    <option value="pending">Pending review</option>
                    <option value="generation_issue">Generation issue</option>
                    <option value="generated">Generated</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Letter type</label>
                  <select
                    className="select-admin w-full"
                    value={filters.type}
                    onChange={(event) =>
                      setFilters((current) => ({
                        ...current,
                        type: event.target.value,
                      }))}
                  >
                    <option value="">All letter types</option>
                    {DOCUMENT_REQUEST_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        )}

        {isLoading ? (
          <SelfServiceWorklistSkeleton />
        ) : isError ? (
          <div className="bg-white rounded-2xl border border-gray-200">
            <div className="text-center py-12 px-6">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Document queue could not be loaded
              </h3>
              <p className="text-sm text-gray-500">
                No requests were changed. Try loading the queue again.
              </p>
              <button
                type="button"
                onClick={() => refetch()}
                className="btn btn-admin-primary mt-4"
              >
                Try again
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-4 lg:gap-6 lg:flex-1 lg:min-h-0 lg:overflow-hidden">
            <div
              ref={listPaneRef}
              className={`${
                isCompact && selectedId ? 'hidden' : 'space-y-3'
              } lg:min-h-0 lg:overflow-y-auto lg:overscroll-y-contain lg:pr-1`}
            >
              {visibleItems.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-200">
                  <div className="text-center py-12 px-6">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      No matching document requests
                    </h3>
                    <p className="text-sm text-gray-500">
                      Adjust the search or filters to see other records.
                    </p>
                  </div>
                </div>
              ) : (
                visibleItems.map((item) => (
                  <QueueCard
                    key={`${item.itemType}-${item.id}`}
                    item={item}
                    selected={
                      selectedId === String(item.id)
                      && selectedKind === item.itemType
                    }
                    onSelect={selectItem}
                  />
                ))
              )}
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
                totalCount={filteredItems.length}
                pageSize={PAGE_SIZE}
                theme="admin"
                noun="records"
                variant="standalone"
              />
            </div>

            <div
              ref={detailPaneRef}
              className={`${
                isCompact && !selectedId ? 'hidden' : ''
              } lg:min-h-0 lg:h-full lg:overflow-y-auto lg:overscroll-y-contain lg:pr-1`}
            >
              {isCompact && selectedId && (
                <button
                  type="button"
                  onClick={closeDetail}
                  className="btn btn-admin-outline mb-3"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to requests
                </button>
              )}

              {!selectedId ? (
                <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-sm text-gray-500 lg:h-full lg:flex lg:flex-col lg:items-center lg:justify-center">
                  <FileText className="w-10 h-10 text-gray-300 mb-3" />
                  Select a request to review the employee details and PDF.
                </div>
              ) : !selectedItem ? (
                <div className="bg-white rounded-2xl border border-gray-200">
                  <div className="text-center py-12 px-6">
                    <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Record not found
                    </h3>
                    <p className="text-sm text-gray-500">
                      It may have moved after another HR action.
                    </p>
                    <button
                      type="button"
                      onClick={closeDetail}
                      className="btn btn-admin-outline mt-4"
                    >
                      Back to queue
                    </button>
                  </div>
                </div>
              ) : selectedItem.itemType === 'request' ? (
                <RequestDetail
                  item={selectedItem}
                  onApprove={() =>
                    setConfirm({ type: 'approve', item: selectedItem })}
                  onReject={() =>
                    setConfirm({ type: 'reject', item: selectedItem })}
                  onRetry={() =>
                    setConfirm({ type: 'retry', item: selectedItem })}
                  approvePending={approve.isPending}
                  retryPending={retry.isPending}
                />
              ) : (
                <LetterDetail
                  item={selectedItem}
                  onDownload={downloadLetter}
                  onRegenerate={() =>
                    setConfirm({ type: 'regenerate', item: selectedItem })}
                  downloading={downloading}
                  regenerating={regenerate.isPending}
                />
              )}
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={!!confirm}
        title={
          confirm?.type === 'approve'
            ? 'Approve and generate this letter?'
            : confirm?.type === 'reject'
              ? 'Reject this letter request?'
              : confirm?.type === 'retry'
                ? 'Retry letter generation?'
                : 'Restore this letter PDF?'
        }
        confirmLabel={
          confirm?.type === 'approve'
            ? 'Approve and generate'
            : confirm?.type === 'reject'
              ? 'Reject request'
              : confirm?.type === 'retry'
                ? 'Retry generation'
                : 'Restore PDF'
        }
        variant={confirm?.type === 'reject' ? 'danger' : 'primary'}
        onConfirm={handleConfirm}
        onCancel={() => {
          if (mutationPending) return;
          setConfirm(null);
          setRejectionReason('');
        }}
        isLoading={mutationPending}
        theme="admin"
      >
        {confirm?.type === 'approve' && (
          <div className="space-y-2 text-sm text-gray-700">
            <p>
              This commits HR’s approval and generates the employee-facing PDF.
            </p>
            <p>
              If generation fails, the request will remain pending and the employee will not be notified.
            </p>
          </div>
        )}
        {confirm?.type === 'reject' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              The employee will see this reason and can submit a corrected request.
            </p>
            <div>
              <label
                htmlFor="document-request-rejection-reason"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Reason for rejection *
              </label>
              <textarea
                id="document-request-rejection-reason"
                rows={4}
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:border-red-500"
                placeholder="Explain what needs to be corrected..."
                value={rejectionReason}
                onChange={(event) => setRejectionReason(event.target.value)}
              />
            </div>
          </div>
        )}
        {confirm?.type === 'retry' && (
          <p className="text-sm text-gray-700">
            The current template and employee data will be used to generate the missing PDF.
          </p>
        )}
        {confirm?.type === 'regenerate' && (
          <p className="text-sm text-gray-700">
            Restore the retained audit record’s PDF for an authorised HR purpose.
          </p>
        )}
      </ConfirmModal>
    </>
  );
};

export default DocumentRequestQueue;
